import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  Award, Star, Flame, TrendingUp, Zap, Crown, Users, PlayCircle,
  Scissors, Trophy, Target
} from 'lucide-react';
import axios from 'axios';
import { useAuth, API } from '../App';
import Navbar from '../components/Navbar';

const ICON_MAP = {
  'play-circle': PlayCircle, 'scissors': Scissors, 'star': Star,
  'award': Award, 'crown': Crown, 'users': Users, 'flame': Flame,
  'trending-up': TrendingUp, 'zap': Zap, 'trophy': Trophy, 'target': Target,
};

const BadgeIcon = ({ icon, color, size = 24, earned = false }) => {
  const Icon = ICON_MAP[icon] || Award;
  return (
    <div className={`p-3 rounded-full ${earned ? '' : 'grayscale opacity-40'}`} style={{ backgroundColor: earned ? `${color}20` : 'rgba(255,255,255,0.05)' }}>
      <Icon size={size} style={{ color: earned ? color : '#666' }} />
    </div>
  );
};

const GamificationPage = () => {
  const { i18n } = useTranslation();
  const { user, token } = useAuth();
  const el = i18n.language === 'el';
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('badges');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        if (token) {
          await axios.post(`${API}/gamification/daily-login`, {}, { headers }).catch(() => {});
        }

        const [statsRes, lbRes] = await Promise.all([
          token ? axios.get(`${API}/gamification/my-stats`, { headers }) : Promise.resolve({ data: null }),
          axios.get(`${API}/gamification/leaderboard`, { headers }),
        ]);
        setStats(statsRes.data || null);
        setLeaderboard(lbRes.data || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-misel-bg">
        <Navbar />
        <div className="pt-24 px-8 md:px-16 pb-16 flex justify-center items-center">
          <div className="animate-pulse text-gray-500">{el ? 'Φόρτωση...' : 'Loading...'}</div>
        </div>
      </div>
    );
  }

  const level = stats?.level;
  const nextLevel = level?.next_level;
  const progressToNext = nextLevel
    ? Math.min(100, Math.round(((stats.total_points - level.min_points) / (nextLevel.min_points - level.min_points)) * 100))
    : 100;
  const recentActivity = stats?.recent_activity || stats?.recent_points || [];
  const leaderboardHasPoints = leaderboard.some((entry) => (entry.total_points || 0) > 0);

  return (
    <div className="min-h-screen bg-misel-bg" data-testid="gamification-page">
      <Navbar />
      <div className="pt-24 px-8 md:px-16 pb-16 max-w-6xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="font-roboto font-bold text-3xl text-white tracking-wide mb-2">
            {el ? 'Επιτεύγματα' : 'Achievements'}
          </h1>
          <p className="text-gray-400 mb-8">{el ? 'Κέρδισε πόντους, ξεκλείδωσε badges, ανέβα στην κατάταξη!' : 'Earn points, unlock badges, climb the leaderboard!'}</p>

          {/* Level & Points Bar */}
          {stats && (
            <div className="bg-misel-card border border-white/10 rounded-sm p-6 mb-8" data-testid="level-card">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="text-2xl font-bold text-primary">{level?.level}</span>
                  </div>
                  <div>
                    <p className="text-white font-bold text-xl">{el ? level?.name_el : level?.name_en}</p>
                    <p className="text-gray-400 text-sm">{stats.total_points} {el ? 'πόντοι' : 'points'}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-gray-500 text-sm">{el ? 'Badges' : 'Badges'}</p>
                  <p className="text-white font-bold text-xl">{stats.earned_count}/{stats.total_badges}</p>
                </div>
                <div className="text-right">
                  <p className="text-gray-500 text-sm">Streak</p>
                  <p className="text-white font-bold text-xl flex items-center gap-1">
                    <Flame size={18} className="text-orange-400" /> {stats.login_streak}
                  </p>
                </div>
              </div>
              
              {/* Progress bar to next level */}
              {nextLevel && (
                <div>
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Level {level.level}</span>
                    <span>{stats.total_points} / {nextLevel.min_points} {el ? 'πόντοι' : 'pts'}</span>
                    <span>Level {nextLevel.level} ({el ? nextLevel.name_el : nextLevel.name_en})</span>
                  </div>
                  <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full bg-gradient-to-r from-primary to-red-400 rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${progressToNext}%` }}
                      transition={{ duration: 1, ease: 'easeOut' }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 mb-6 bg-misel-card border border-white/10 rounded-sm p-1 w-fit">
            {[
              { key: 'badges', label: el ? 'Badges' : 'Badges' },
              { key: 'leaderboard', label: el ? 'Κατάταξη' : 'Leaderboard' },
              { key: 'activity', label: el ? 'Δραστηριότητα' : 'Activity' },
            ].map(tab => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)} data-testid={`tab-${tab.key}`}
                className={`px-5 py-2 text-sm rounded-sm transition-colors ${activeTab === tab.key ? 'bg-primary text-white' : 'text-gray-400 hover:text-white'}`}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* BADGES TAB */}
          {activeTab === 'badges' && stats && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {stats.badges?.length > 0 ? stats.badges.map((badge, i) => (
                <motion.div
                  key={badge.slug}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={`bg-misel-card border rounded-sm p-5 ${badge.earned ? 'border-white/20' : 'border-white/5'}`}
                  data-testid={`badge-${badge.slug}`}
                >
                  <div className="flex items-start gap-4">
                    <BadgeIcon icon={badge.icon} color={badge.color} earned={badge.earned} />
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium ${badge.earned ? 'text-white' : 'text-gray-500'}`}>
                        {el ? badge.name_el : badge.name_en}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {el ? badge.description_el : badge.description_en}
                      </p>
                      {badge.reward_type && badge.reward_value && (
                        <p className="text-xs text-primary mt-1">
                          {el ? 'Ανταμοιβή' : 'Reward'}: {badge.reward_value}% {el ? 'έκπτωση' : 'off'}
                        </p>
                      )}
                      {badge.earned && badge.earned_at && (
                        <p className="text-xs text-green-400 mt-1">{badge.earned_at.slice(0, 10)}</p>
                      )}
                    </div>
                  </div>
                  {/* Progress bar */}
                  {!badge.earned && (
                    <div className="mt-3">
                      <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${badge.progress}%`, backgroundColor: badge.color }} />
                      </div>
                      <p className="text-xs text-gray-600 mt-1">{badge.progress}%</p>
                    </div>
                  )}
                  {badge.earned && (
                    <div className="mt-3 flex items-center gap-1 text-green-400 text-xs">
                      <Award size={12} /> {el ? 'Κερδήθηκε!' : 'Earned!'}
                    </div>
                  )}
                </motion.div>
              )) : (
                <div className="col-span-full bg-misel-card border border-white/10 rounded-sm p-12 text-center text-gray-500">
                  {el ? 'Δεν υπάρχουν ενεργά badges ακόμα.' : 'No active badges yet.'}
                </div>
              )}
            </div>
          )}

          {activeTab === 'badges' && !stats && (
            <div className="bg-misel-card border border-white/10 rounded-sm p-12 text-center text-gray-500">
              {el ? 'Συνδέσου για να δεις τα προσωπικά σου badges και πόντους.' : 'Sign in to see your badges and personal points.'}
            </div>
          )}

          {/* LEADERBOARD TAB */}
          {activeTab === 'leaderboard' && (
            <div className="bg-misel-card border border-white/10 rounded-sm" data-testid="leaderboard">
              {leaderboard.length > 0 && leaderboardHasPoints ? (
                <div className="divide-y divide-white/5">
                  {leaderboard.map((entry) => (
                    <div key={entry.user_id} className={`p-4 flex items-center gap-4 ${entry.user_id === user?.id ? 'bg-primary/10' : ''}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        entry.rank === 1 ? 'bg-yellow-500/20 text-yellow-400' :
                        entry.rank === 2 ? 'bg-gray-300/20 text-gray-300' :
                        entry.rank === 3 ? 'bg-amber-600/20 text-amber-500' :
                        'bg-white/5 text-gray-500'
                      }`}>
                        {entry.rank <= 3 ? ['', '1', '2', '3'][entry.rank] : entry.rank}
                      </div>
                      <div className="flex-1">
                        <p className="text-white font-medium text-sm">{entry.name}</p>
                        <p className="text-xs text-gray-500">Level {entry.level} · {el ? entry.level_name_el : entry.level_name_en}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-bold">{entry.total_points}</p>
                        <p className="text-xs text-gray-500">{entry.badge_count} badges</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center text-gray-500">
                  {el ? 'Δεν υπάρχει δραστηριότητα πόντων ακόμα. Ολοκλήρωσε ένα βίντεο ή κάνε καθημερινή σύνδεση για να ξεκινήσει η κατάταξη.' : 'No point activity yet. Complete a video or check in daily to start the leaderboard.'}
                </div>
              )}
            </div>
          )}

          {/* ACTIVITY TAB */}
          {activeTab === 'activity' && stats && (
            <div className="bg-misel-card border border-white/10 rounded-sm" data-testid="activity-log">
              {recentActivity.length > 0 ? (
                <div className="divide-y divide-white/5">
                  {recentActivity.map((a) => (
                    <div key={a.id} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <TrendingUp size={14} className="text-primary" />
                        </div>
                        <div>
                          <p className="text-white text-sm">
                            {a.reason || a.action?.replace?.('_', ' ') || a.source_type?.replace?.(/_/g, ' ') || (el ? 'Δραστηριότητα πόντων' : 'Point activity')}
                          </p>
                          <p className="text-xs text-gray-500">{a.created_at?.slice(0, 10)}</p>
                        </div>
                      </div>
                      <span className="text-green-400 font-bold text-sm">+{a.points}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-12 text-center text-gray-500">
                  {el ? 'Δεν υπάρχει δραστηριότητα πόντων ακόμα. Οι ολοκληρώσεις βίντεο και οι καθημερινές συνδέσεις θα εμφανίζονται εδώ.' : 'No point activity yet. Video completions and daily check-ins will appear here.'}
                </div>
              )}
            </div>
          )}

          {activeTab === 'activity' && !stats && (
            <div className="bg-misel-card border border-white/10 rounded-sm p-12 text-center text-gray-500">
              {el ? 'Συνδέσου για να δεις τη δραστηριότητά σου.' : 'Sign in to see your activity.'}
            </div>
          )}

          {/* Points Guide */}
          <div className="mt-8 bg-misel-card border border-white/10 rounded-sm p-6">
            <h3 className="text-white font-medium mb-4">{el ? 'Πώς κερδίζω πόντους;' : 'How do I earn points?'}</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { action: el ? 'Ολοκλήρωση βίντεο' : 'Complete video', pts: 10, icon: PlayCircle },
                { action: el ? 'Αγορά συνδρομής' : 'Subscribe', pts: 50, icon: Crown },
                { action: el ? 'Παραπομπή φίλου' : 'Refer a friend', pts: 100, icon: Users },
                { action: el ? 'Καθημερινή σύνδεση' : 'Daily login', pts: 5, icon: Flame },
              ].map((item, i) => (
                <div key={i} className="bg-white/5 rounded-sm p-3 text-center">
                  <item.icon size={20} className="text-primary mx-auto mb-2" />
                  <p className="text-white text-sm font-medium">+{item.pts}</p>
                  <p className="text-gray-500 text-xs">{item.action}</p>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GamificationPage;
